/**
 * NeuGreen Energy — Intelligent Energy Ecosystem Germany
 * 12 native slides, EN + DE. Executive B2B tone, German engineering style.
 * Brand slogan: "Unabhängigkeit durch Innovation" / "Independence through Innovation"
 */

export const SLIDES = [
  // -------------------------------------------------------------- 1 · Cover
  {
    kind: "cover",
    id: "cover",
    n: 1,
    i18n: {
      en: {
        eyebrow: "Corporate Briefing · 2026",
        title: "NeuGreen Energy",
        subtitle: "Intelligent Energy Ecosystem Germany",
        slogan: "Independence through Innovation",
        tag: "Solar · Storage · Mobility · Digital",
      },
      de: {
        eyebrow: "Unternehmens-Briefing · 2026",
        title: "NeuGreen Energy",
        subtitle: "Intelligentes Energieökosystem Deutschland",
        slogan: "Unabhängigkeit durch Innovation",
        tag: "Solar · Speicher · Mobilität · Digital",
      },
      pt: {
        eyebrow: "Briefing Corporativo · 2026",
        title: "NeuGreen Energy",
        subtitle: "Ecossistema Energético Inteligente Alemanha",
        slogan: "Independência através da Inovação",
        tag: "Solar · Armazenamento · Mobilidade · Digital",
      },
    },
  },

  // -------------------------------------------------------------- 2 · Vision
  {
    kind: "vision",
    id: "vision",
    n: 2,
    i18n: {
      en: {
        eyebrow: "Our Vision",
        title: "Connecting global renewable technology with the German market.",
        lede: "NeuGreen Energy is a German energy technology integrator — not a reseller. We select, engineer and combine best-in-class international components into one intelligent ecosystem, tailored to the technical, regulatory and cultural requirements of Germany.",
        pillars: [
          { t: "Mission", d: "Accelerate energy independence through integrated solar, storage, mobility and digital solutions." },
          { t: "Method",  d: "Deep engineering integration — not distribution. Every component is validated, documented and supported." },
          { t: "Focus",   d: "Serve installers, commercial & industrial clients and public projects across Germany and neighbouring EU markets." },
        ],
      },
      de: {
        eyebrow: "Unsere Vision",
        title: "Globale Erneuerbare-Technologie trifft deutschen Markt.",
        lede: "NeuGreen Energy ist ein deutscher Energietechnik-Integrator — kein Wiederverkäufer. Wir wählen aus, entwickeln und kombinieren erstklassige internationale Komponenten zu einem intelligenten Ökosystem, das den technischen, regulatorischen und kulturellen Anforderungen Deutschlands entspricht.",
        pillars: [
          { t: "Mission", d: "Energieunabhängigkeit durch integrierte Solar-, Speicher-, Mobilitäts- und Digitallösungen beschleunigen." },
          { t: "Methode", d: "Tiefe Systemintegration — kein Vertrieb. Jede Komponente wird validiert, dokumentiert und unterstützt." },
          { t: "Fokus",   d: "Installateure, Gewerbe-, Industrie- und öffentliche Projekte in Deutschland und angrenzenden EU-Märkten." },
        ],
      },
      pt: {
        eyebrow: "A Nossa Visão",
        title: "Tecnologia renovável global ao serviço do mercado alemão.",
        lede: "A NeuGreen Energy é um integrador alemão de tecnologia energética — não um revendedor. Selecionamos, engenhamos e combinamos componentes internacionais de topo num ecossistema inteligente, adaptado às exigências técnicas, regulatórias e culturais da Alemanha.",
        pillars: [
          { t: "Missão", d: "Acelerar a independência energética através de soluções integradas de solar, armazenamento, mobilidade e digital." },
          { t: "Método", d: "Integração profunda de engenharia — não distribuição. Cada componente é validada, documentada e suportada." },
          { t: "Foco",   d: "Servir instaladores, clientes comerciais e industriais e projetos públicos na Alemanha e nos mercados UE vizinhos." },
        ],
      },
    },
  },

  // -------------------------------------------------------------- 3 · Market Challenge
  {
    kind: "challenge",
    id: "market",
    n: 3,
    i18n: {
      en: {
        eyebrow: "Market Challenge · Germany",
        title: "The Energiewende is a systems problem.",
        lede: "German households and businesses want energy independence, but face a fragmented technology landscape and heavy regulation.",
        points: [
          { k: "€0.42/kWh", v: "Industrial electricity price — one of the highest in the EU" },
          { k: "72%",       v: "of German consumers demand energy independence by 2030" },
          { k: "5+",        v: "vendors typically required for one integrated system" },
          { k: "18 mo",     v: "average grid-connection lead time for commercial PV" },
          { k: "+34%/y",    v: "growth in EV adoption — new charging infrastructure needed" },
        ],
      },
      de: {
        eyebrow: "Marktherausforderung · Deutschland",
        title: "Die Energiewende ist ein Systemproblem.",
        lede: "Deutsche Haushalte und Unternehmen wollen Energieunabhängigkeit, stoßen aber auf eine fragmentierte Technologielandschaft und dichte Regulierung.",
        points: [
          { k: "0,42 €/kWh", v: "Industriestrompreis — einer der höchsten in der EU" },
          { k: "72 %",       v: "der deutschen Verbraucher fordern bis 2030 Energieunabhängigkeit" },
          { k: "5+",         v: "Anbieter typischerweise nötig für ein integriertes System" },
          { k: "18 Mon.",    v: "durchschnittliche Netzanschlussdauer für gewerbliche PV" },
          { k: "+34 %/J",    v: "Wachstum der E-Mobilität — neue Ladeinfrastruktur erforderlich" },
        ],
      },
      pt: {
        eyebrow: "Desafio de Mercado · Alemanha",
        title: "A Energiewende é um problema de sistema.",
        lede: "As famílias e empresas alemãs querem independência energética, mas enfrentam um panorama tecnológico fragmentado e regulação pesada.",
        points: [
          { k: "0,42 €/kWh", v: "Preço industrial da eletricidade — dos mais elevados da UE" },
          { k: "72 %",       v: "dos consumidores alemães exigem independência energética até 2030" },
          { k: "5+",         v: "fornecedores tipicamente necessários para um sistema integrado" },
          { k: "18 meses",   v: "prazo médio de ligação à rede para PV comercial" },
          { k: "+34 %/ano",  v: "crescimento da adoção de VE — nova infraestrutura de carregamento necessária" },
        ],
      },
    },
  },

  // -------------------------------------------------------------- 4 · Ecosystem Diagram
  {
    kind: "ecosystem",
    id: "ecosystem",
    n: 4,
    i18n: {
      en: {
        eyebrow: "The NeuGreen Ecosystem",
        title: "One integrated platform. Three physical layers. One brain.",
        lede: "Instead of five independent vendors, the customer gets one contract, one dashboard and one point of accountability. The AI energy manager makes every layer work together — solar meets storage, storage meets mobility, all under one system.",
        center:  { t: "NeuGreen Energy",     s: "German Market Integrator" },
        brain:   { t: "AI Energy Management", s: "Central Orchestration Brain" },
        branches: [
          { t: "Solar PV",       s: "Energy Generation Layer", color: "green" },
          { t: "Energy Storage", s: "Energy Storage Layer",    color: "blue"  },
          { t: "EV Mobility",    s: "Energy Mobility Layer",   color: "silver" },
        ],
      },
      de: {
        eyebrow: "Das NeuGreen Ökosystem",
        title: "Eine integrierte Plattform. Drei physische Ebenen. Ein Gehirn.",
        lede: "Statt fünf unabhängiger Anbieter erhält der Kunde einen Vertrag, ein Dashboard und einen Ansprechpartner. Der KI-Energiemanager verbindet alle Ebenen — Solar trifft Speicher, Speicher trifft Mobilität, alles unter einem System.",
        center:  { t: "NeuGreen Energy",       s: "Deutscher Markt-Integrator" },
        brain:   { t: "KI-Energiemanagement",  s: "Zentrales Orchestrierungs-Gehirn" },
        branches: [
          { t: "Solar PV",     s: "Energieerzeugungs-Ebene", color: "green" },
          { t: "Energiespeicher", s: "Energiespeicher-Ebene",   color: "blue"  },
          { t: "E-Mobilität",  s: "Energiemobilitäts-Ebene",  color: "silver" },
        ],
      },
      pt: {
        eyebrow: "O Ecossistema NeuGreen",
        title: "Uma plataforma integrada. Três camadas físicas. Um cérebro.",
        lede: "Em vez de cinco fornecedores independentes, o cliente recebe um contrato, um dashboard e um único ponto de responsabilidade. O gestor de energia IA faz cada camada trabalhar em conjunto — solar encontra armazenamento, armazenamento encontra mobilidade, tudo sob um sistema.",
        center:  { t: "NeuGreen Energy",       s: "Integrador de Mercado Alemão" },
        brain:   { t: "Gestão de Energia IA",  s: "Cérebro Central de Orquestração" },
        branches: [
          { t: "Solar PV",            s: "Camada de Geração de Energia",    color: "green" },
          { t: "Armazenamento",       s: "Camada de Armazenamento",         color: "blue"  },
          { t: "Mobilidade Elétrica", s: "Camada de Mobilidade Energética", color: "silver" },
        ],
      },
    },
  },

  // -------------------------------------------------------------- 5 · Solar
  {
    kind: "layer",
    id: "solar",
    n: 5,
    layer: "solar",
    supplier: "Bluesun Solar",
    role: { en: "Energy Generation Layer", de: "Energieerzeugungs-Ebene" },
    i18n: {
      en: {
        eyebrow: "Solar Generation",
        title: "N-Type TOPCon modules — engineered for German rooftops.",
        lede: "Our generation layer is built on high-efficiency, low-degradation N-Type TOPCon modules from Bluesun Solar. Selected for German climate, roof geometries and Einspeisemanagement compliance.",
        pillars: [
          { t: "N-Type TOPCon Modules",  d: "Up to 23.2% cell efficiency, 30-year linear performance warranty, low light performance for German winters." },
          { t: "Residential Systems",    d: "Full 4–15 kWp systems designed for single-family homes with Einfamilienhaus roof standards." },
          { t: "Commercial PV",          d: "50 kWp – 750 kWp systems for warehouses, agricultural and industrial rooftops." },
          { t: "Hybrid Solutions",       d: "Solar + storage-ready inverters, prepared for retrofit or day-one installation." },
        ],
      },
      de: {
        eyebrow: "Solarerzeugung",
        title: "N-Type TOPCon-Module — für deutsche Dächer entwickelt.",
        lede: "Unsere Erzeugungs-Ebene basiert auf hocheffizienten N-Type TOPCon-Modulen von Bluesun Solar mit geringer Degradation. Ausgewählt für deutsches Klima, Dachgeometrien und Einspeisemanagement-Konformität.",
        pillars: [
          { t: "N-Type TOPCon-Module",  d: "Bis 23,2 % Zelleneffizienz, 30 Jahre lineare Leistungsgarantie, gute Schwachlicht-Performance für deutsche Winter." },
          { t: "Wohnsysteme",           d: "Komplette 4–15 kWp-Anlagen, ausgelegt auf Einfamilienhaus-Dachstandards." },
          { t: "Gewerbe-PV",            d: "50 kWp – 750 kWp Anlagen für Lager, landwirtschaftliche und industrielle Dächer." },
          { t: "Hybrid-Lösungen",       d: "Solar + speicherfähige Wechselrichter, vorbereitet für Nachrüstung oder Erstinstallation." },
        ],
      },
      pt: {
        eyebrow: "Geração Solar",
        title: "Módulos N-Type TOPCon — desenhados para telhados alemães.",
        lede: "A nossa camada de geração assenta em módulos N-Type TOPCon de alta eficiência e baixa degradação da Bluesun Solar. Selecionados para o clima alemão, geometrias de telhado e conformidade com Einspeisemanagement.",
        pillars: [
          { t: "Módulos N-Type TOPCon", d: "Até 23,2 % de eficiência de célula, 30 anos de garantia linear, boa performance em luz fraca para invernos alemães." },
          { t: "Sistemas Residenciais", d: "Sistemas completos de 4–15 kWp desenhados para moradias unifamiliares (Einfamilienhaus)." },
          { t: "PV Comercial",          d: "Sistemas de 50 kWp a 750 kWp para armazéns, telhados agrícolas e industriais." },
          { t: "Soluções Híbridas",     d: "Inversores solar + prontos para armazenamento, preparados para retrofit ou instalação inicial." },
        ],
      },
    },
  },

  // -------------------------------------------------------------- 6 · Storage
  {
    kind: "layer",
    id: "storage",
    n: 6,
    layer: "storage",
    supplier: "Wattzing New Energy Technology",
    role: { en: "Energy Storage Layer", de: "Energiespeicher-Ebene", pt: "Camada de Armazenamento" },
    i18n: {
      en: {
        eyebrow: "Energy Storage",
        title: "Modular battery systems — from 5 kWh home to 500 kWh commercial.",
        lede: "The storage layer is delivered on Wattzing New Energy Technology's ESS platform. LFP chemistry, modular architecture and OEM flexibility give NeuGreen Energy the ability to configure a system for almost any German use case.",
        pillars: [
          { t: "ESS Systems",          d: "Complete Energy Storage Systems from 5 kWh residential to 500 kWh commercial." },
          { t: "Battery Solutions",    d: "LFP (LiFePO₄) chemistry — safe, 6,000+ cycles, no thermal runaway risk." },
          { t: "Smart Energy Products", d: "Integrated BMS, cloud monitoring, remote firmware updates." },
          { t: "OEM Capabilities",     d: "White-label options for larger installers who want their own branded stack." },
        ],
      },
      de: {
        eyebrow: "Energiespeicher",
        title: "Modulare Batteriesysteme — von 5 kWh Zuhause bis 500 kWh Gewerbe.",
        lede: "Die Speicher-Ebene basiert auf der ESS-Plattform von Wattzing New Energy Technology. LFP-Chemie, modulare Architektur und OEM-Flexibilität ermöglichen NeuGreen Energy nahezu jede Konfiguration für den deutschen Markt.",
        pillars: [
          { t: "ESS-Systeme",              d: "Komplette Energiespeichersysteme von 5 kWh Wohnbereich bis 500 kWh Gewerbe." },
          { t: "Batterielösungen",         d: "LFP-Chemie (LiFePO₄) — sicher, 6.000+ Zyklen, kein Thermal-Runaway-Risiko." },
          { t: "Smart-Energy-Produkte",    d: "Integriertes BMS, Cloud-Monitoring, Fernwartung per Firmware-Update." },
          { t: "OEM-Möglichkeiten",        d: "White-Label-Optionen für größere Installateure mit eigenem Markenauftritt." },
        ],
      },
      pt: {
        eyebrow: "Armazenamento de Energia",
        title: "Baterias modulares — de 5 kWh doméstico a 500 kWh comercial.",
        lede: "A camada de armazenamento é entregue sobre a plataforma ESS da Wattzing New Energy Technology. Química LFP, arquitetura modular e flexibilidade OEM permitem à NeuGreen configurar qualquer caso de uso alemão.",
        pillars: [
          { t: "Sistemas ESS",           d: "Sistemas completos de armazenamento de 5 kWh residencial a 500 kWh comercial." },
          { t: "Soluções de Bateria",    d: "Química LFP (LiFePO₄) — segura, 6.000+ ciclos, sem risco de fuga térmica." },
          { t: "Produtos Smart Energy",  d: "BMS integrado, monitorização em nuvem, atualizações remotas de firmware." },
          { t: "Capacidades OEM",        d: "Opções white-label para instaladores maiores que queiram a sua própria marca." },
        ],
      },
    },
  },

  // -------------------------------------------------------------- 7 · Mobility
  {
    kind: "layer",
    id: "mobility",
    n: 7,
    layer: "mobility",
    supplier: "BENY New Energy",
    role: { en: "Energy Mobility Layer", de: "Energiemobilitäts-Ebene" },
    i18n: {
      en: {
        eyebrow: "Energy Infrastructure & EV Mobility",
        title: "Charge the car with sunlight — safely.",
        lede: "The mobility layer runs on BENY New Energy hardware — EV chargers, DC/AC protection devices and solar-side balance-of-system components. It closes the loop from PV panel to car battery in a single certified chain.",
        pillars: [
          { t: "EV Chargers",           d: "AC 11/22 kW Wallboxes and DC 30–180 kW fast chargers, OCPP 1.6/2.0.1 native." },
          { t: "DC/AC Protection",      d: "Rapid shutdown, arc-fault detection, VDE-AR-N 4100 compliant switchgear." },
          { t: "Solar Components",      d: "String combiners, PV isolators, monitoring gateways — the invisible layer." },
          { t: "Energy Infrastructure", d: "Full BoS packages: cabling, protection, junction boxes, meters, all documented." },
        ],
      },
      de: {
        eyebrow: "Energieinfrastruktur & E-Mobilität",
        title: "Das Auto mit Sonnenlicht laden — sicher.",
        lede: "Die Mobilitäts-Ebene läuft auf Hardware von BENY New Energy — EV-Ladegeräte, DC/AC-Schutzeinrichtungen und Solar-Balance-of-System-Komponenten. Sie schließt den Kreis vom PV-Modul zur Autobatterie in einer zertifizierten Kette.",
        pillars: [
          { t: "EV-Ladegeräte",         d: "AC 11/22 kW Wallboxen und DC 30–180 kW Schnelllader, nativ OCPP 1.6/2.0.1." },
          { t: "DC/AC-Schutz",          d: "Schnellabschaltung, Lichtbogen-Erkennung, VDE-AR-N 4100-konforme Schaltanlage." },
          { t: "Solar-Komponenten",     d: "String-Combiner, PV-Trenner, Monitoring-Gateways — die unsichtbare Ebene." },
          { t: "Energieinfrastruktur",  d: "Komplette BoS-Pakete: Verkabelung, Schutz, Klemmkästen, Zähler, alles dokumentiert." },
        ],
      },
      pt: {
        eyebrow: "Infraestrutura Energética & Mobilidade Elétrica",
        title: "Carregar o carro com luz solar — em segurança.",
        lede: "A camada de mobilidade corre sobre hardware BENY New Energy — carregadores VE, proteção DC/AC e componentes balance-of-system solar. Fecha o ciclo do painel PV à bateria do carro numa cadeia única e certificada.",
        pillars: [
          { t: "Carregadores VE",           d: "Wallboxes AC 11/22 kW e carregadores rápidos DC 30–180 kW, OCPP 1.6/2.0.1 nativo." },
          { t: "Proteção DC/AC",            d: "Corte rápido, deteção de arco elétrico, quadros conformes VDE-AR-N 4100." },
          { t: "Componentes Solares",       d: "Combinadores de string, seccionadores PV, gateways de monitorização — a camada invisível." },
          { t: "Infraestrutura Energética", d: "Pacotes BoS completos: cablagem, proteção, caixas, contadores, tudo documentado." },
        ],
      },
    },
  },

  // -------------------------------------------------------------- 8 · Product Portfolio (BESS + Full Kits)
  {
    kind: "portfolio",
    id: "portfolio",
    n: 8,
    i18n: {
      en: {
        eyebrow: "Product Portfolio & Turnkey Kits",
        title: "From residential to industrial — one integrated portfolio.",
        lede: "NeuGreen Energy is not sold as loose components. Every deployment is packaged as an engineered kit — designed, certified and delivered as a single unit — with a clear upgrade path from residential to megawatt-scale BESS.",
        items: [
          {
            image: "/neugreen/full-kit.jpg",
            badge: "Residential · C&I Kits",
            title: "Full Turnkey Kits",
            supplierLogo: "/neugreen/logo-bluesun.png",
            supplierName: "Bluesun Solar",
            desc: "Solar modules, hybrid inverter, LFP battery, wallbox, DC/AC protection, EMS licence and monitoring — one part number, one commissioning app.",
            specs: ["4–20 kWp solar", "5–30 kWh LFP", "11–22 kW wallbox", "VDE + TÜV certified"],
          },
          {
            image: "/neugreen/bess-commercial.jpg",
            badge: "Commercial · Industrial",
            title: "BESS Containers",
            supplierLogo: "/neugreen/logo-beny.png",
            supplierName: "BENY New Energy",
            desc: "Containerised Battery Energy Storage Systems for warehouses, agriculture, logistics hubs and public buildings — peak-shaving, self-consumption and grid services in one asset.",
            specs: ["100 kWh – 2 MWh", "40-ft & 20-ft containers", "Grid-forming inverter", "IEC 62933 · UL 9540"],
          },
        ],
        note: "Every kit ships with a printed VDE-ready documentation folder and a QR-linked digital twin in the EMS.",
      },
      de: {
        eyebrow: "Produktportfolio & Turnkey-Kits",
        title: "Vom Einfamilienhaus bis zur Industrie — ein integriertes Portfolio.",
        lede: "NeuGreen Energy wird nicht als Einzelkomponenten verkauft. Jeder Einsatz wird als konstruiertes Kit geliefert — entworfen, zertifiziert und als eine Einheit ausgeliefert — mit klarem Upgrade-Pfad vom Wohnbereich bis zum Megawatt-BESS.",
        items: [
          {
            image: "/neugreen/full-kit.jpg",
            badge: "Wohnbereich · Gewerbe",
            title: "Komplette Turnkey-Kits",
            supplierLogo: "/neugreen/logo-bluesun.png",
            supplierName: "Bluesun Solar",
            desc: "Solarmodule, Hybrid-Wechselrichter, LFP-Batterie, Wallbox, DC/AC-Schutz, EMS-Lizenz und Monitoring — eine Artikelnummer, eine Inbetriebnahme-App.",
            specs: ["4–20 kWp Solar", "5–30 kWh LFP", "11–22 kW Wallbox", "VDE + TÜV zertifiziert"],
          },
          {
            image: "/neugreen/bess-commercial.jpg",
            badge: "Gewerbe · Industrie",
            title: "BESS-Container",
            supplierLogo: "/neugreen/logo-beny.png",
            supplierName: "BENY New Energy",
            desc: "Containerisierte Batteriespeichersysteme für Lager, Landwirtschaft, Logistik-Hubs und öffentliche Gebäude — Peak-Shaving, Eigenverbrauch und Netzdienstleistungen in einem Asset.",
            specs: ["100 kWh – 2 MWh", "40-ft & 20-ft Container", "Netzbildender Wechselrichter", "IEC 62933 · UL 9540"],
          },
        ],
        note: "Jedes Kit enthält einen gedruckten VDE-fertigen Dokumentationsordner und einen QR-verknüpften Digital Twin im EMS.",
      },
      pt: {
        eyebrow: "Portfólio de Produtos & Kits Chave-na-Mão",
        title: "Do residencial ao industrial — um portfólio integrado.",
        lede: "A NeuGreen Energy não é vendida como componentes soltos. Cada implantação é entregue como um kit de engenharia — desenhado, certificado e entregue como uma unidade única — com caminho claro de upgrade do residencial até BESS de escala megawatt.",
        items: [
          {
            image: "/neugreen/full-kit.jpg",
            badge: "Residencial · Comercial",
            title: "Kits Chave-na-Mão Completos",
            supplierLogo: "/neugreen/logo-bluesun.png",
            supplierName: "Bluesun Solar",
            desc: "Módulos solares, inversor híbrido, bateria LFP, wallbox, proteção DC/AC, licença EMS e monitorização — um único part number, uma app de comissionamento.",
            specs: ["4–20 kWp solar", "5–30 kWh LFP", "11–22 kW wallbox", "Certificado VDE + TÜV"],
          },
          {
            image: "/neugreen/bess-commercial.jpg",
            badge: "Comercial · Industrial",
            title: "Containers BESS",
            supplierLogo: "/neugreen/logo-beny.png",
            supplierName: "BENY New Energy",
            desc: "Sistemas de Armazenamento de Energia containerizados para armazéns, agricultura, hubs logísticos e edifícios públicos — peak-shaving, auto-consumo e serviços de rede num só ativo.",
            specs: ["100 kWh – 2 MWh", "Containers 40-ft & 20-ft", "Inversor formador de rede", "IEC 62933 · UL 9540"],
          },
        ],
        note: "Cada kit inclui uma pasta impressa de documentação pronta VDE e um digital twin ligado por QR na plataforma EMS.",
      },
    },
  },

  // -------------------------------------------------------------- 9 · EMS Platform
  {
    kind: "ems",
    id: "ems",
    n: 8,
    i18n: {
      en: {
        eyebrow: "Digital Energy Management",
        title: "The NeuGreen EMS Platform.",
        lede: "A single cloud-native dashboard — accessible from web, iOS and Android — where the installer commissions the system, the owner sees savings in real time, and the AI optimises every kWh.",
        panels: [
          { t: "Real-time Monitoring",         d: "Live power flows across solar, battery, grid and EV — 1-second resolution." },
          { t: "Solar Production Analysis",    d: "Yield vs. forecast, string-level diagnostics, weather-normalised performance." },
          { t: "Battery Health Monitoring",    d: "State of Charge, State of Health, cycle counting, thermal telemetry per module." },
          { t: "EV Charging Optimisation",     d: "Charge only from surplus solar; smart-tariff scheduling; V2H-ready roadmap." },
          { t: "Smart Energy Control",         d: "AI decides in real time whether to store, self-consume, sell to grid or charge the car." },
          { t: "Reports & Compliance",         d: "Automatic monthly reports, Einspeisemanagement logs, tax-office ready exports." },
        ],
      },
      de: {
        eyebrow: "Digitales Energiemanagement",
        title: "Die NeuGreen EMS-Plattform.",
        lede: "Ein einziges Cloud-natives Dashboard — verfügbar in Web, iOS und Android — auf dem der Installateur das System in Betrieb nimmt, der Besitzer Einsparungen live sieht und die KI jede kWh optimiert.",
        panels: [
          { t: "Echtzeit-Monitoring",           d: "Live-Energieflüsse zwischen Solar, Batterie, Netz und E-Fahrzeug — 1-Sekunden-Auflösung." },
          { t: "Solar-Produktionsanalyse",      d: "Ertrag vs. Prognose, String-Ebenen-Diagnose, wetterbereinigte Performance." },
          { t: "Batterie-Zustandsüberwachung",  d: "State of Charge, State of Health, Zyklenzählung, thermische Telemetrie pro Modul." },
          { t: "Ladeoptimierung E-Fahrzeuge",   d: "Nur aus Solar-Überschuss laden; Smart-Tarif-Planung; V2H-fähige Roadmap." },
          { t: "Intelligente Energiesteuerung", d: "KI entscheidet in Echtzeit: speichern, selbst verbrauchen, ins Netz einspeisen oder ins Auto laden." },
          { t: "Berichte & Compliance",         d: "Automatische Monatsberichte, Einspeisemanagement-Logs, finanzamttaugliche Exporte." },
        ],
      },
      pt: {
        eyebrow: "Gestão Digital de Energia",
        title: "A Plataforma NeuGreen EMS.",
        lede: "Um único dashboard cloud-native — web, iOS e Android — onde o instalador põe o sistema em serviço, o proprietário vê poupanças em tempo real e a IA otimiza cada kWh.",
        panels: [
          { t: "Monitorização em Tempo Real",   d: "Fluxos de potência ao vivo entre solar, bateria, rede e VE — resolução de 1 segundo." },
          { t: "Análise de Produção Solar",     d: "Produção vs. previsão, diagnóstico por string, performance normalizada por meteorologia." },
          { t: "Saúde da Bateria",              d: "State of Charge, State of Health, contagem de ciclos, telemetria térmica por módulo." },
          { t: "Otimização de Carregamento VE", d: "Carregar apenas com excedente solar; agendamento por tarifário; roadmap V2H." },
          { t: "Controlo Inteligente",          d: "IA decide em tempo real: armazenar, auto-consumir, vender à rede ou carregar o carro." },
          { t: "Relatórios & Compliance",       d: "Relatórios mensais automáticos, logs Einspeisemanagement, exports para contabilidade." },
        ],
      },
    },
  },

  // -------------------------------------------------------------- 9 · Technology Network
  {
    kind: "network",
    id: "network",
    n: 9,
    i18n: {
      en: {
        eyebrow: "Global Technology Network",
        title: "Selected international suppliers. Delivered as one German solution.",
        lede: "NeuGreen Energy cooperates with a carefully selected group of international technology suppliers to deliver reliable energy solutions for Germany and Europe. Each supplier is evaluated on quality, certification, supply-chain stability and technical support. Presented here as technology sources — not as commercial partnerships.",
        nodes: [
          { t: "Bluesun Solar",                s: "Solar PV modules & inverters",         area: "CN · IEC · CE · TÜV" },
          { t: "Wattzing New Energy",          s: "ESS & battery platforms",              area: "CN · CE · IEC · UN 38.3" },
          { t: "BENY New Energy",              s: "EV chargers & DC/AC protection",       area: "CN · CE · VDE · TÜV" },
          { t: "NeuGreen Engineering (DE)",    s: "System design & EMS software",         area: "DE · Meister-Certified" },
          { t: "German Installer Network",     s: "Local installation & service",         area: "DE · SHK · Elektro-Innung" },
          { t: "European Logistics",           s: "Warehousing & distribution",           area: "DE · NL · PL hubs" },
        ],
      },
      de: {
        eyebrow: "Globales Technologie-Netzwerk",
        title: "Ausgewählte internationale Lieferanten. Als eine deutsche Lösung geliefert.",
        lede: "NeuGreen Energy kooperiert mit einer sorgfältig ausgewählten Gruppe internationaler Technologielieferanten, um zuverlässige Energielösungen für Deutschland und Europa zu liefern. Jeder Lieferant wird nach Qualität, Zertifizierung, Lieferkettenstabilität und technischem Support bewertet. Hier als Technologiequellen dargestellt — nicht als kommerzielle Partnerschaften.",
        nodes: [
          { t: "Bluesun Solar",                s: "Solar-PV-Module & Wechselrichter",     area: "CN · IEC · CE · TÜV" },
          { t: "Wattzing New Energy",          s: "ESS- & Batterie-Plattformen",          area: "CN · CE · IEC · UN 38.3" },
          { t: "BENY New Energy",              s: "EV-Ladegeräte & DC/AC-Schutz",         area: "CN · CE · VDE · TÜV" },
          { t: "NeuGreen Engineering (DE)",    s: "Systemdesign & EMS-Software",          area: "DE · Meister-zertifiziert" },
          { t: "Deutsches Installateur-Netz",  s: "Lokale Installation & Service",        area: "DE · SHK · Elektro-Innung" },
          { t: "Europäische Logistik",         s: "Lager & Distribution",                 area: "DE · NL · PL Hubs" },
        ],
      },
      pt: {
        eyebrow: "Rede Global de Tecnologia",
        title: "Fornecedores internacionais selecionados. Entregues como uma solução alemã.",
        lede: "A NeuGreen Energy coopera com um grupo cuidadosamente selecionado de fornecedores internacionais de tecnologia para entregar soluções energéticas fiáveis para a Alemanha e Europa. Cada fornecedor é avaliado em qualidade, certificação, estabilidade de cadeia de fornecimento e suporte técnico. Aqui apresentados como fontes de tecnologia — não como parcerias comerciais.",
        nodes: [
          { t: "Bluesun Solar",               s: "Módulos e inversores solar PV",         area: "CN · IEC · CE · TÜV" },
          { t: "Wattzing New Energy",         s: "Plataformas ESS e baterias",            area: "CN · CE · IEC · UN 38.3" },
          { t: "BENY New Energy",             s: "Carregadores VE e proteção DC/AC",      area: "CN · CE · VDE · TÜV" },
          { t: "NeuGreen Engineering (DE)",   s: "Design de sistemas e software EMS",     area: "DE · Certificação Meister" },
          { t: "Rede Alemã de Instaladores",  s: "Instalação e serviço local",            area: "DE · SHK · Elektro-Innung" },
          { t: "Logística Europeia",          s: "Armazenamento e distribuição",          area: "DE · NL · PL Hubs" },
        ],
      },
    },
  },

  // -------------------------------------------------------------- 10 · Value
  {
    kind: "value",
    id: "value",
    n: 10,
    i18n: {
      en: {
        eyebrow: "Value Proposition",
        title: "Two audiences. One integrated answer.",
        lede: "Every design decision inside NeuGreen Energy is measured against two simple questions: does it make the installer's day easier, and does it lower the customer's cost of energy?",
        columns: [
          {
            head: "For Installers",
            items: [
              { t: "Complete Documentation",   d: "Every project ships with schematics, VDE forms, commissioning reports — ready for grid operator submission." },
              { t: "Technical Support",        d: "German-speaking L2/L3 engineers on call. Remote diagnostics via EMS." },
              { t: "Reliable Supply Chain",    d: "German warehouse stock for the top 30 SKUs; 48h delivery on standard configs." },
              { t: "Simplified Project Management", d: "One quote, one delivery, one commissioning app — instead of five vendors." },
            ],
          },
          {
            head: "For Customers",
            items: [
              { t: "One Integrated Solution",  d: "A single system, a single warranty owner, a single dashboard." },
              { t: "Energy Independence",      d: "Self-consumption rates typically 70%+ with solar + storage + smart charging." },
              { t: "Lower Operating Costs",    d: "Modelled 4–7 year payback for typical German C&I profile." },
              { t: "Future-ready Technology",  d: "V2H, dynamic tariff and heat-pump-ready — no re-installation needed." },
            ],
          },
        ],
      },
      de: {
        eyebrow: "Wertversprechen",
        title: "Zwei Zielgruppen. Eine integrierte Antwort.",
        lede: "Jede Design-Entscheidung bei NeuGreen Energy wird an zwei einfachen Fragen gemessen: Macht sie den Arbeitstag des Installateurs einfacher, und senkt sie die Energiekosten des Kunden?",
        columns: [
          {
            head: "Für Installateure",
            items: [
              { t: "Vollständige Dokumentation", d: "Jedes Projekt mit Schaltplänen, VDE-Formularen, Inbetriebnahmeprotokollen — netzbetreiberfertig." },
              { t: "Technischer Support",        d: "Deutschsprachige L2/L3-Ingenieure erreichbar. Ferndiagnose via EMS." },
              { t: "Zuverlässige Lieferkette",   d: "Deutscher Lagerbestand für die Top-30-SKUs; 48h-Lieferung bei Standardkonfigurationen." },
              { t: "Vereinfachtes Projektmanagement", d: "Ein Angebot, eine Lieferung, eine Inbetriebnahme-App — statt fünf Anbieter." },
            ],
          },
          {
            head: "Für Kunden",
            items: [
              { t: "Eine integrierte Lösung",    d: "Ein System, ein Garantieträger, ein Dashboard." },
              { t: "Energieunabhängigkeit",      d: "Eigenverbrauchsquoten typischerweise 70 %+ mit Solar + Speicher + Smart Charging." },
              { t: "Geringere Betriebskosten",   d: "Modelliertes 4–7 Jahre Payback für typisches deutsches C&I-Profil." },
              { t: "Zukunftssichere Technik",    d: "V2H, dynamischer Tarif und wärmepumpenfähig — keine Neuinstallation nötig." },
            ],
          },
        ],
      },
      pt: {
        eyebrow: "Proposta de Valor",
        title: "Dois públicos. Uma resposta integrada.",
        lede: "Cada decisão de design dentro da NeuGreen Energy é medida contra duas perguntas simples: torna o dia do instalador mais fácil, e reduz o custo de energia do cliente?",
        columns: [
          {
            head: "Para Instaladores",
            items: [
              { t: "Documentação Completa",           d: "Cada projeto inclui esquemas, formulários VDE, relatórios de comissionamento — pronto para submeter ao operador de rede." },
              { t: "Suporte Técnico",                 d: "Engenheiros L2/L3 em alemão disponíveis. Diagnóstico remoto via EMS." },
              { t: "Cadeia de Fornecimento Fiável",   d: "Stock em armazém alemão para os 30 SKUs de topo; entrega em 48h nas configurações padrão." },
              { t: "Gestão de Projeto Simplificada",  d: "Uma proposta, uma entrega, uma app de comissionamento — em vez de cinco fornecedores." },
            ],
          },
          {
            head: "Para Clientes",
            items: [
              { t: "Solução Integrada Única",           d: "Um sistema, um dono de garantia, um dashboard." },
              { t: "Independência Energética",          d: "Taxas de auto-consumo tipicamente 70 %+ com solar + armazenamento + carregamento inteligente." },
              { t: "Custos Operacionais Mais Baixos",   d: "Payback modelado de 4–7 anos para perfil C&I alemão típico." },
              { t: "Tecnologia Preparada para o Futuro", d: "V2H, tarifa dinâmica e prontos para bomba de calor — sem re-instalação." },
            ],
          },
        ],
      },
    },
  },

  // -------------------------------------------------------------- 11 · Business Model
  {
    kind: "business",
    id: "business",
    n: 11,
    i18n: {
      en: {
        eyebrow: "Business Model",
        title: "Germany Market Integrator.",
        lede: "NeuGreen Energy is not a distributor and not a manufacturer. It sits between them — the technical and commercial interface that turns global components into deployable German systems.",
        streams: [
          { t: "Technology Integration",         d: "Curate, validate and system-certify international components for the German market (VDE, TÜV, BSI)." },
          { t: "Project Development",            d: "End-to-end delivery for commercial and industrial customers — feasibility, engineering, procurement, commissioning." },
          { t: "Installer Network",              d: "Certified installer program with training, tooling, and preferential pricing for approved partners." },
          { t: "Commercial & Industrial Solutions", d: "Turnkey solar + storage + EV for warehouses, agriculture, logistics and municipal buildings." },
          { t: "Strategic Cooperation",          d: "Long-term technology & supply agreements with selected international suppliers." },
        ],
        note: "Revenue diversified across integration margin, project development fees, service contracts and EMS software licences.",
      },
      de: {
        eyebrow: "Geschäftsmodell",
        title: "Deutscher Markt-Integrator.",
        lede: "NeuGreen Energy ist weder Distributor noch Hersteller. Es steht dazwischen — die technische und kommerzielle Schnittstelle, die globale Komponenten in einsatzfähige deutsche Systeme verwandelt.",
        streams: [
          { t: "Technologie-Integration",           d: "Kuration, Validierung und Systemzertifizierung internationaler Komponenten für den deutschen Markt (VDE, TÜV, BSI)." },
          { t: "Projektentwicklung",                d: "End-to-End-Lieferung für Gewerbe- und Industriekunden — Machbarkeit, Engineering, Beschaffung, Inbetriebnahme." },
          { t: "Installateur-Netzwerk",             d: "Zertifiziertes Installateurprogramm mit Schulung, Werkzeug und Vorzugspreisen für zugelassene Partner." },
          { t: "Gewerbe- & Industrielösungen",      d: "Schlüsselfertig Solar + Speicher + E-Fahrzeug für Lager, Landwirtschaft, Logistik und kommunale Gebäude." },
          { t: "Strategische Kooperation",          d: "Langfristige Technologie- und Lieferverträge mit ausgewählten internationalen Lieferanten." },
        ],
        note: "Umsatz diversifiziert über Integrationsmarge, Projektentwicklungsgebühren, Serviceverträge und EMS-Software-Lizenzen.",
      },
      pt: {
        eyebrow: "Modelo de Negócio",
        title: "Integrador de Mercado Alemão.",
        lede: "A NeuGreen Energy não é distribuidora nem fabricante. Fica no meio — a interface técnica e comercial que transforma componentes globais em sistemas alemães implantáveis.",
        streams: [
          { t: "Integração de Tecnologia",           d: "Curar, validar e certificar como sistema os componentes internacionais para o mercado alemão (VDE, TÜV, BSI)." },
          { t: "Desenvolvimento de Projetos",        d: "Entrega end-to-end para clientes comerciais e industriais — viabilidade, engenharia, aquisição, comissionamento." },
          { t: "Rede de Instaladores",               d: "Programa certificado de instaladores com formação, ferramentas e preços preferenciais para parceiros aprovados." },
          { t: "Soluções Comerciais e Industriais",  d: "Solar + armazenamento + VE chave-na-mão para armazéns, agricultura, logística e edifícios municipais." },
          { t: "Cooperação Estratégica",             d: "Acordos de longo prazo em tecnologia e fornecimento com fornecedores internacionais selecionados." },
        ],
        note: "Receita diversificada por margem de integração, taxas de desenvolvimento de projeto, contratos de serviço e licenças de software EMS.",
      },
    },
  },

  // -------------------------------------------------------------- 12 · Future & Contact
  {
    kind: "contact",
    id: "contact",
    n: 12,
    i18n: {
      en: {
        eyebrow: "Future Vision",
        title: "Building the intelligent energy infrastructure of tomorrow.",
        lede: "The next decade of German energy will be defined by integration — not by more panels or more batteries, but by the intelligence that ties everything together. NeuGreen Energy exists to build that intelligence, on German soil, for the German market and its European neighbours.",
        contactTitle: "Business Development & Strategic Cooperation",
        contact: {
          brand:    "NeuGreen Energy",
          country:  "Germany",
          email:    "cooperation@neugreen.energy",
          web:      "neugreen.energy",
          note:     "For manufacturer inquiries, installer program applications, C&I projects and investor discussions.",
        },
        cta: "Schedule an executive briefing",
      },
      de: {
        eyebrow: "Zukunftsvision",
        title: "Wir bauen die intelligente Energieinfrastruktur von morgen.",
        lede: "Das nächste Jahrzehnt der deutschen Energie wird nicht durch mehr Module oder mehr Batterien geprägt, sondern durch die Intelligenz, die alles zusammenhält. NeuGreen Energy existiert, um diese Intelligenz zu bauen — auf deutschem Boden, für den deutschen Markt und seine europäischen Nachbarn.",
        contactTitle: "Business Development & Strategische Kooperation",
        contact: {
          brand:    "NeuGreen Energy",
          country:  "Deutschland",
          email:    "cooperation@neugreen.energy",
          web:      "neugreen.energy",
          note:     "Für Anfragen von Herstellern, Installateur-Programmen, Gewerbe-/Industrieprojekten und Investoren.",
        },
        cta: "Executive-Briefing vereinbaren",
      },
      pt: {
        eyebrow: "Visão de Futuro",
        title: "A construir a infraestrutura energética inteligente de amanhã.",
        lede: "A próxima década da energia alemã será definida pela integração — não por mais painéis ou mais baterias, mas pela inteligência que liga tudo. A NeuGreen Energy existe para construir essa inteligência, em solo alemão, para o mercado alemão e os seus vizinhos europeus.",
        contactTitle: "Business Development & Cooperação Estratégica",
        contact: {
          brand:    "NeuGreen Energy",
          country:  "Alemanha",
          email:    "cooperation@neugreen.energy",
          web:      "neugreen.energy",
          note:     "Para consultas de fabricantes, candidaturas ao programa de instaladores, projetos C&I e conversas com investidores.",
        },
        cta: "Marcar briefing executivo",
      },
    },
  },
];

export const UI = {
  en: {
    prev: "Previous", next: "Next", slide: "Slide",
    intro: "Corporate Briefing — Confidential",
    back: "Home", play: "Auto-play",
    footer: "NeuGreen Energy · Germany · Independence through Innovation",
  },
  de: {
    prev: "Zurück", next: "Weiter", slide: "Folie",
    intro: "Unternehmens-Briefing — Vertraulich",
    back: "Startseite", play: "Autoplay",
    footer: "NeuGreen Energy · Deutschland · Unabhängigkeit durch Innovation",
  },
  pt: {
    prev: "Anterior", next: "Próximo", slide: "Slide",
    intro: "Briefing Corporativo — Confidencial",
    back: "Início", play: "Reprodução automática",
    footer: "NeuGreen Energy · Alemanha · Independência através da Inovação",
  },
};

export const SUPPORTED = ["en", "de", "pt"];

/** Brand palette */
export const PALETTE = {
  graphite:  "#0F1114",   // background
  ink:       "#E4E7EB",   // primary text
  dim:       "rgba(228,231,235,0.62)",
  green:     "#22C55E",   // energy green
  greenSoft: "#8CE0AA",
  blue:      "#3B82F6",   // technology blue
  blueSoft:  "#93B7FF",
  silver:    "#C0C7CE",   // metallic silver
  white:     "#FFFFFF",
  border:    "rgba(192,199,206,0.14)",
  surface:   "rgba(192,199,206,0.04)",
};
